<template>
  <v-card class="order-details-card rounded-lg" outlined>
    <v-card-title class="order-details-card__title">Order details</v-card-title>
    <v-divider class="order-details-card__divider" />
    <v-card-text class="order-details-card__body">
      <div class="order-details__box">
        <span class="order-details__box-label">Order #</span>
        <span class="order-details__box-num">{{ orderNumber }}</span>
      </div>
      <div class="order-details__box order-details__box--list">
        <div class="order-details__line-items-heading">
          <span class="order-details__box-label order-details__box-label--list-title">Line items</span>
          <span class="order-details__box-label order-details__box-label--list-count">{{ lineItems.length }}</span>
        </div>
        <div v-if="lineItems.length" class="order-details__line-items-scroll">
          <v-list dense class="order-details__list">
            <v-list-item
              v-for="(lineItem, idx) in lineItems"
              :key="idx"
              class="order-details__list-item"
            >
              <v-list-item-content class="order-details__list-content">
                <v-list-item-title class="order-details__list-title">
                  {{ lineItem.title || lineItem.name || 'Item' }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="lineItem.quantity" class="order-details__list-qty">
                  Qty: {{ lineItem.quantity }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
        <p v-if="!lineItems.length" class="order-details__empty">No line items</p>
      </div>
      <div v-if="customer" class="order-details__box">
        <span class="order-details__box-label">Customer</span>
        <div class="order-details__box-value">
          <span class="order-details__box-primary">{{ customerName }}</span>
          <template v-if="customer.email">
            <span class="order-details__box-meta">{{ customer.email }}</span>
          </template>
          <template v-if="customer.phone">
            <span class="order-details__box-meta">{{ customer.phone }}</span>
          </template>
        </div>
      </div>
      <div v-if="shippingAddress" class="order-details__box">
        <span class="order-details__box-label">Delivery</span>
        <div class="order-details__box-value order-details__box-value--prewrap">{{ shippingAddress }}</div>
      </div>
      <div v-if="orderNote" class="order-details__box">
        <span class="order-details__box-label">Notes</span>
        <div class="order-details__box-value order-details__box-value--prewrap">{{ orderNote }}</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { getEngravingLineItems } from './orderHelpers'

export default {
  name: 'OrderDetailsCard',
  props: {
    order: { type: Object, default: () => ({}) },
  },
  computed: {
    orderNumber() {
      return this.order?.orderNumber || '—'
    },
    lineItems() {
      return getEngravingLineItems(this.order)
    },
    customer() {
      return this.order?.customer || null
    },
    customerName() {
      const c = this.customer
      if (!c) return '—'
      return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—'
    },
    shippingAddress() {
      const s = this.order?.shippingAddress || {}
      const parts = [
        s.address1,
        s.address2,
        [s.city, s.province].filter(Boolean).join(', '),
        s.zip,
        s.country,
      ].filter(Boolean)
      return parts.length ? parts.join('\n') : null
    },
    orderNote() {
      const n = this.order?.note || this.order?.notes || ''
      return typeof n === 'string' && n.trim() ? n.trim() : null
    },
  },
}
</script>

<style scoped>
.order-details-card {
  height: 100%;
  min-height: 280px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  text-align: left;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.order-details-card__title {
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.12em !important;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85) !important;
  padding: 20px 22px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.order-details-card__divider {
  display: none;
}

.order-details-card__body {
  padding: 18px 20px 22px !important;
  min-width: 0;
  max-width: 100%;
}

.order-details__box {
  padding: 14px 16px;
  min-width: 0;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  margin-bottom: 14px;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.order-details__box:last-child {
  margin-bottom: 0;
}

.order-details__box:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.09);
}

.order-details__box-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
  line-height: 1.2;
}

.order-details__line-items-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  min-width: 0;
}

.order-details__box-label--list-title {
  margin-bottom: 0 !important;
  flex: 1;
  min-width: 0;
}

.order-details__box-label--list-count {
  margin-bottom: 0 !important;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.875rem;
  padding: 5px 12px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.08em;
  line-height: 1;
  color: rgba(255, 255, 255, 0.96);
  background: linear-gradient(
    165deg,
    rgba(66, 165, 245, 0.28) 0%,
    rgba(25, 118, 210, 0.18) 45%,
    rgba(13, 71, 161, 0.22) 100%
  );
  border: 1px solid rgba(100, 181, 246, 0.38);
  border-radius: 999px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
}

.order-details__box-num {
  display: inline-block;
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #fff;
  line-height: 1.2;
  padding: 4px 0;
}

.order-details__box-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.55;
  letter-spacing: 0.01em;
}

.order-details__box-value--prewrap {
  white-space: pre-wrap;
  word-break: break-word;
}

.order-details__box-primary {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 4px;
  letter-spacing: 0.01em;
}

.order-details__box-meta {
  display: block;
  font-size: 0.8125rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.45;
  margin-top: 2px;
}

.order-details__box-meta:first-of-type {
  margin-top: 8px;
}

.order-details__box--list {
  padding-bottom: 10px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.order-details__box--list:hover {
  background: rgba(255, 255, 255, 0.04);
}

.order-details__list {
  background: transparent !important;
}

.order-details__list-item {
  min-height: 48px;
  padding: 10px 12px !important;
  margin: 0;
  border-radius: 8px;
  border-bottom: none;
  align-items: flex-start;
  transition: background 0.15s ease;
}

.order-details__list-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.order-details__list-item:last-child {
  border-bottom: none;
}

.order-details__list-content {
  padding: 0 !important;
  min-width: 0;
  overflow: hidden;
}

/* Wrap text inside this column; Vuetify list defaults are nowrap + ellipsis (truncates on the right). */
.order-details__list >>> .v-list-item__title,
.order-details__list >>> .v-list-item__subtitle {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  overflow: visible !important;
  text-overflow: clip !important;
}

.order-details__list-title {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: rgba(255, 255, 255, 0.92) !important;
  line-height: 1.4;
  letter-spacing: 0.01em;
}

.order-details__list-qty {
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  color: rgba(255, 255, 255, 0.5) !important;
  margin-top: 4px;
}

.order-details__line-items-scroll {
  max-height: 200px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 8px;
  padding-right: 2px;
  -webkit-overflow-scrolling: touch;
}

.order-details__line-items-scroll .order-details__list {
  margin-top: 0;
}

.order-details__line-items-scroll::-webkit-scrollbar {
  width: 6px;
}

.order-details__line-items-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 3px;
}

.order-details__line-items-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.order-details__empty {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.38);
  margin: 12px 0 0;
  font-style: italic;
  line-height: 1.5;
}
</style>
